import { trace, type Attributes } from '@opentelemetry/api';
import type { EgAssistantUsageEvent } from '@expertgroup/assistant';
/**
 * OpenTelemetry browser tracing — the frontend half of distributed tracing.
 *
 * Patches the global `fetch` (which Angular's `HttpClient` uses via `withFetch()`) so every API call becomes a
 * span carrying the W3C `traceparent` header. The backend's ASP.NET Core instrumentation continues that trace,
 * so a click → API call → DB query is ONE distributed trace in the collector (otel-ext → Fabric).
 *
 * Zoneless-critical: we use `StackContextManager`, NOT `ZoneContextManager` — the latter depends on zone.js,
 * which this app does not load (`provideZonelessChangeDetection()`), and would silently break context propagation.
 *
 * Must run BEFORE `bootstrapApplication` (see main.ts) so the tracer is registered before the first HTTP call.
 * No-op when `environment.collectorUrl` is empty (e.g. prod until a collector exists) — telemetry is opt-in.
 */
import { WebTracerProvider, StackContextManager } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { environment } from '@/environments/environment';

// Matches the ecosystem topology naming — the backend node is 'ExpertGroup-protax-service', the SPA is its '-ui' peer.
const SERVICE_NAME = 'ExpertGroup-protax-ui';

export function initTelemetry(): void {
    // SSR / non-browser guard (the app is client-only today, but keep telemetry from ever running server-side).
    if (typeof window === 'undefined') return;

    const collectorUrl = environment.collectorUrl?.trim();
    if (!collectorUrl) return; // telemetry disabled — no collector configured for this environment.

    const provider = new WebTracerProvider({
        resource: resourceFromAttributes({
            [ATTR_SERVICE_NAME]: SERVICE_NAME,
            [ATTR_SERVICE_VERSION]: '0.0.0'
        }),
        // 2.x API: span processors go in the constructor (the old provider.addSpanProcessor() was removed in 2.0).
        spanProcessors: [new BatchSpanProcessor(new OTLPTraceExporter({ url: collectorUrl }))]
    });

    provider.register({ contextManager: new StackContextManager() });

    registerInstrumentations({
        instrumentations: [
            new FetchInstrumentation({
                // Inject `traceparent` on calls to OUR api only. Same-origin (the dev proxy '/api') is propagated by
                // default; an absolute apiUrl (staging/prod) is cross-origin and must be matched explicitly here.
                propagateTraceHeaderCorsUrls: apiPropagationUrls(environment.apiUrl),
                // Don't trace the exporter's own POSTs to the collector (avoid a self-referential span loop).
                ignoreUrls: [new RegExp('^' + escapeRegExp(collectorUrl))],
                clearTimingResources: true
            })
        ]
    });

    // BatchSpanProcessor buffers spans; flush the last batch when the tab is hidden so SPA spans aren't lost on nav.
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') void provider.forceFlush();
    });
}

/**
 * Origins whose requests should carry `traceparent`: the API origin when `apiUrl` is absolute.
 *
 * ⚠️ Nothing else belongs here — least of all the Identity issuer. The brand-by-host request that main.ts makes
 * before bootstrap (`GET {issuer}/branding/by-host`, @expertgroup/shell/brand) must stay a CORS "simple request":
 * a `traceparent` header would force a preflight the endpoint does not answer, and the app would silently fall back to
 * the generic look. `otel.spec.ts` pins that the issuer origin of every environment is outside this list.
 */
export function apiPropagationUrls(apiUrl: string): RegExp[] {
    if (/^https?:\/\//i.test(apiUrl)) {
        try {
            return [new RegExp('^' + escapeRegExp(new URL(apiUrl).origin))];
        } catch {
            return [];
        }
    }
    // Relative apiUrl ('/api') => same-origin; FetchInstrumentation injects traceparent on same-origin by default.
    // (The collector is intentionally NOT propagated to — the exporter POST must not carry an app traceparent.)
    return [];
}

function escapeRegExp(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Assistant product events contain identifiers of configured chat types, counts and timing only. */
export function recordAssistantUsage(event: EgAssistantUsageEvent): void {
    if (typeof window === 'undefined' || !environment.collectorUrl?.trim()) return;
    const attributes: Attributes = { 'assistant.event': event.kind };
    if (event.profileId) attributes['assistant.profile'] = event.profileId;
    if (event.chatId) attributes['assistant.chat'] = event.chatId;
    if (event.durationMs !== undefined) attributes['assistant.duration_ms'] = event.durationMs;
    if (event.rating) attributes['assistant.rating'] = event.rating;
    const span = trace.getTracer('expertgroup.assistant').startSpan('assistant.usage', { attributes });
    span.end();
}
